import { useQuery } from "@tanstack/react-query";
import { useAxios } from "hooks/useAxios";
import { USER_API } from "config";
import { workspaceKeys } from "queries/workspace.keys";
import { normalizeId } from "utils";
import { useCallback, useMemo } from "react";
import {
  UseWorkspacePermissionsResult,
  WorkspacePermissionsResponse,
  WorkspaceRole,
  WorkspacePermissions,
  UpgradeInfo,
} from "./workspace.types";
import { createUpgradeInfo } from "./workspaceUpgradeUtils";
import { WorkspacePayload } from "./workspace.types";
import {
  Permissions,
  canWorkspace,
  type WorkspacePermission,
} from "utils/permissions/permissionConstants";
import { useSessionUser } from "hooks/useSession";

// Type guard to check if workspace has permissions
const hasWorkspacePermissions = (
  data: any
): data is WorkspacePayload & {
  permissions: {
    permissions: string[];
    role: string;
    workspace_id: string;
    user_id: string;
  };
} => {
  return data && typeof data === "object" && "permissions" in data && data.permissions;
};

/**
 * Hook for fetching workspace permissions
 * Uses consolidated endpoint approach - permissions are included in main workspace data
 *
 * This is the new approach that reduces API calls and improves performance
 */
export const useWorkspacePermissions = (
  workspaceId: string | number
): UseWorkspacePermissionsResult => {
  const axios = useAxios({ allResponse: false });
  const normalizedWorkspaceId = normalizeId(workspaceId);

  // Use the main workspace query which now includes permissions
  const { data, isLoading, isError, error, refetch } = useQuery<WorkspacePermissionsResponse>({
    queryKey: workspaceKeys.detail(normalizedWorkspaceId), // Use main workspace query key
    queryFn: async () => {
      if (!normalizedWorkspaceId || normalizedWorkspaceId === "") {
        throw new Error("Workspace ID is required");
      }

      try {
        // Fetch workspace data which now includes permissions
        const response = await axios.get(USER_API.getWorkspace(normalizedWorkspaceId));
        return response.data;
      } catch (error: any) {
        // Handle specific error cases
        if (error.response?.status === 403) {
          throw new Error("You don't have access to this workspace");
        }
        if (error.response?.status === 404) {
          throw new Error("Workspace not found");
        }
        throw error;
      }
    },
    enabled: !!normalizedWorkspaceId && normalizedWorkspaceId !== "",
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 403/404 errors
      if (error?.response?.status === 403 || error?.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Extract permissions from workspace data
  const permissionsData = useMemo(() => {
    if (!hasWorkspacePermissions(data)) {
      // Return default permissions if no embedded permissions are available
      return {
        permissions: [Permissions.WORKSPACE_READ],
        role: "viewer",
        workspace_id: String(data?.workspace_id || ""),
        user_id: "",
      };
    }
    return data.permissions;
  }, [data]);

  const { session: profile, isOneTimePurchase } = useSessionUser();

  const workspaceCurrentPlanLabel = useMemo(() => {
    if (isOneTimePurchase) {
      return "One-Time Purchase";
    }
    const name = profile?.pricing_plan?.plan_name?.trim();
    return name && name.length > 0 ? name : "Free";
  }, [isOneTimePurchase, profile?.pricing_plan?.plan_name]);

  // Scoped permission checking - TypeScript enforces workspace permissions only
  const canWorkspacePermission = useCallback(
    (permission: WorkspacePermission): boolean => {
      if (isLoading || !permissionsData?.permissions) return false;
      return canWorkspace(permission, permissionsData.permissions);
    },
    [permissionsData?.permissions, isLoading]
  );

  const hasRole = useCallback(
    (role: WorkspaceRole): boolean => {
      if (isLoading || !permissionsData?.role) return false;
      return permissionsData.role === role;
    },
    [permissionsData?.role, isLoading]
  );

  const getUpgradeInfo = useCallback(
    (permission: WorkspacePermission): UpgradeInfo | null => {
      if (canWorkspacePermission(permission)) return null;

      return createUpgradeInfo(
        permission as string,
        undefined,
        permissionsData?.role as WorkspaceRole,
        workspaceCurrentPlanLabel
      );
    },
    [canWorkspacePermission, permissionsData?.role, workspaceCurrentPlanLabel]
  );

  // Action-based helpers (memoized for performance) - Product-meaningful gates only
  // Core workspace permissions
  const canManageMembers = useMemo(
    () =>
      canWorkspacePermission(Permissions.WORKSPACE_MEMBER_LIST) ||
      canWorkspacePermission(Permissions.WORKSPACE_MEMBER_INVITE) ||
      canWorkspacePermission(Permissions.WORKSPACE_MEMBER_UPDATE) ||
      canWorkspacePermission(Permissions.WORKSPACE_MEMBER_REMOVE),
    [canWorkspacePermission]
  );
  const canCreateProjects = useMemo(
    () => canWorkspacePermission(Permissions.WORKSPACE_PROJECT_CREATE),
    [canWorkspacePermission]
  );

  // Enhanced granular permissions
  const canManageBilling = useMemo(
    () =>
      canWorkspacePermission(Permissions.WORKSPACE_BILLING_READ) ||
      canWorkspacePermission(Permissions.WORKSPACE_BILLING_UPDATE),
    [canWorkspacePermission]
  );

  // Member management permissions - Product-meaningful helpers
  const canInviteMembers = useMemo(
    () => canWorkspacePermission(Permissions.WORKSPACE_MEMBER_INVITE),
    [canWorkspacePermission]
  );
  const canRemoveMembers = useMemo(
    () => canWorkspacePermission(Permissions.WORKSPACE_MEMBER_REMOVE),
    [canWorkspacePermission]
  );
  const canUpdateMembers = useMemo(
    () => canWorkspacePermission(Permissions.WORKSPACE_MEMBER_UPDATE),
    [canWorkspacePermission]
  );
  const canViewMembers = useMemo(
    () => canWorkspacePermission(Permissions.WORKSPACE_MEMBER_LIST),
    [canWorkspacePermission]
  );

  // Convert permission strings to boolean object
  const permissions: WorkspacePermissions = useMemo(() => {
    if (!permissionsData?.permissions) {
      return (_permission: string) => false; // Default: no permissions
    }

    const permissionStrings: string[] = permissionsData.permissions;
    return (permission: string) => permissionStrings.includes(permission);
  }, [permissionsData?.permissions]);

  return {
    permissions,
    role: (permissionsData?.role as WorkspaceRole) || null,
    workspaceId: permissionsData?.workspace_id || null,
    userId: permissionsData?.user_id || null,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
    // Scoped permission checking - TypeScript enforces workspace permissions only
    canWorkspace: canWorkspacePermission,
    hasRole,
    getUpgradeInfo,

    // Core workspace permissions (product-meaningful helpers)
    canManageMembers,
    canCreateProjects,

    // Enhanced granular permissions
    canManageBilling,

    // Member management permissions
    canInviteMembers,
    canRemoveMembers,
    canUpdateMembers,
    canViewMembers,
  };
};
